import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';

import { AnalyticsService } from '../../../core/services/analytics.service';
import { BooksService } from '../../../core/services/data/books.service';
import { GenreService } from '../../../core/services/data/genre.service';
import { GiveawayService } from '../../../core/services/data/giveaway.service';
import { LocalStorageService } from '../../../core/services/data/local-storage.service';
import { ICheckoutOrderData, OrderService, TempInventoryResponse } from '../../../core/services/data/order.service';
import { PromoStackPurchaseService } from '../../../core/services/data/promo-stack-purchase.service';
import { PurchaseProcessService } from '../../../core/services/data/purchase-process.service';
import { SavedPaymentsMethodsService } from '../../../core/services/data/saved-payment-methods.service';
import { SettingsService } from '../../../core/services/data/settings.service';
import { UserService } from '../../../core/services/data/user.service';
import { LogsService } from '../../../core/services/logs.service';
import { ModalService } from '../../../core/services/modal.service';
import { StripeService } from '../../../core/services/stripe.service';
import { IOrder, IOrderFeatureItem, IOrderPromoStacksItem } from '../../../types/datatypes';
import { ProcessPaymentEvent } from '../../../types/local';
import { IPopulatedGenres } from '../../../types/populated';
import { MembershipUpgradeService } from '../../../core/services/data/membership-upgrade.service';
import { PromoCodeService } from '../../../core/services/data/promo-code.service';
import { ProcessPaymentAPIResponse } from '../../../types/responses';
import { CheckoutService } from '../../../core/services/checkout.service';

const AUTHOR_DASHBOARD = '/author-dashboard';
const FEATURES_PATH = '/features';

const PROMO_TYPE_PERCENT = 'percent';
const PROMO_TYPE_AMOUNT = 'amount';

@Component({
	selector: 'app-features-cart-checkout',
	templateUrl: './features-cart-checkout.component.html',
	styleUrls: ['../features-step5/features-step5.component.scss', './features-cart-checkout.component.scss'],
})
export class FeaturesCartCheckoutComponent implements OnInit, OnDestroy {
	public order: IOrder;

	public orderTotalAmount = 0;

	public order$;

	public paymentInProgress = false;

	public purchaseInProgressMessages = [
		'Processing Payment Please Wait',
		'Reserving Your Features Please Wait',
		'Finalizing Your Order Please Wait',
		'Sending Your Receipt Please Wait',
	];

	public learnMoreAboutMembershipLink;

	public updateInProgress = false;

	public loadingOrder = false;

	public purchaseInProgressMessage = 'Processing Payment Please Wait';

	public errorTitle;

	public errorText;

	public errorExtra;

	public soldOut = false;

	public stripeCheckoutEnabled = false;

	public genres: IPopulatedGenres[];

	public qualifiesForMembershipUpsell = false;

	public checkoutSignupLogin = false;

	public author;

	public errorOptions = {
		title: 'There was a problem',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.ms.close('cart-error-modal'),
		}],
	};

	private refresh$;

	private paypalId;

	private author$;

	constructor(
		private ms: ModalService,
		private os: OrderService,
		private router: Router,
		private route: ActivatedRoute,
		private bs: BooksService,
		private us: UserService,
		private as: AnalyticsService,
		private ss: SettingsService,
		private purchaseProcessService: PurchaseProcessService,
		private ls: LogsService,
		private genreService: GenreService,
		private stripeService: StripeService,
		private promoStackPurchaseService: PromoStackPurchaseService,
		private localStorageService: LocalStorageService,
		private giveawayService: GiveawayService,
		private membershipUpgradeService: MembershipUpgradeService,
		private savedPaymentMethodsService: SavedPaymentsMethodsService,
		private checkoutService: CheckoutService,
		private promoCodeService: PromoCodeService,
	) { }

	ngOnInit() {
		this.os.cartUpdating$.subscribe((data: boolean) => {
			this.updateInProgress = data;
		});
		this.loadingOrder = true;
		this.order$ = this.os.orderStore$.subscribe(async (data: any) => {
			this.order = data;
			this.os.initiateCheckout().subscribe(() => { });
			this.orderTotalAmount = this.os.calculateOrderTotal();
			this.loadingOrder = false;

			const setting = await this.ss.getMembershipUpsellSetting();

			this.qualifiesForMembershipUpsell = this._qualifyForMembershipUpsell(setting);
			this.handleBeginCheckoutAnalytics(data);
		});
		this.author$ = this.us.author$.subscribe(
			(data: any) => {
				this.author = data;
				this.learnMoreAboutMembershipLink = `/author-dashboard/${this.author.authorLink}/subscriptions`;
			},
		);

		this.ss.stripeCheckoutEnabled().then(
			(value) => {
				// this.stripeCheckoutEnabled = !!value;
				this.stripeCheckoutEnabled = !!value;
			},
		);

		this.ss.getCheckoutSignupLoginSetting().then(
			(value) => {
				this.checkoutSignupLogin = !!value;
			},
		);

		this.genreService.getGenres().subscribe((genres: IPopulatedGenres[]) => {
			this.genres = genres;
		});

		this.refresh$ = this.os.orderRefresh.subscribe((orderId) => {
			if (orderId === this.order._id) {
				this.os.refreshCart();
			}
		});
	}

	async ngOnDestroy() {
		this.order$?.unsubscribe();
		this.author$?.unsubscribe();
		this.refresh$?.unsubscribe();
	}

	progressStatusMessages() {
		[this.purchaseInProgressMessage] = this.purchaseInProgressMessages;
		let i = 0;
		const interval = setInterval(() => {
			i += 1;
			if (i === this.purchaseInProgressMessages.length) {
				clearInterval(interval);
				return;
			}
			this.purchaseInProgressMessage = this.purchaseInProgressMessages[i];
		}, 2000);
	}

	addSubscriptionToCart() {
		const goldPlan = '62f27ba988466185204dc935';
		const { purchaserInfo } = this.order;

		const userInfo = {
			username: purchaserInfo.email,
			firstName: purchaserInfo.firstName,
			lastName: purchaserInfo.lastName,
		};

		this.membershipUpgradeService.createCartSubscription(goldPlan, userInfo)
			.subscribe(
				(data: any) => {
					this.closeMembershipUpsell();
				},
			);
	}

	handleBeginCheckoutAnalytics(order: IOrder) {
		this.as.clearEcommerce();
		if (order && order.items && order.items.length) {
			const { items } = order;
			const analyticsCartItems = this.as.generateCartItemsEcommerceObjects(items, (item, ecommItem) => {
				if (item.itemType !== 'featuredBook') {
					return ecommItem;
				}
				const feature = item.item;
				const { _siteAdvert: { _parentGenre } } = feature;
				let parentGenre = 'N/A';

				if (_parentGenre) {
					parentGenre = this.genreService.getGenreNameById(this.genres, _parentGenre);
				}
				ecommItem.item_category = parentGenre;
				return ecommItem;
			});

			this.as.trackBeginCheckout(this.order.totalAmount, analyticsCartItems);
		}
	}

	openMembershipUpsell() {
		this.ms.open('membership-upsell-modal');
	}

	closeMembershipUpsell() {
		this.ms.close('membership-upsell-modal');
	}

	openMembershipRemovalConfirm() {
		this.ms.open('membership-removal-confirmation');
	}

	closeMembershipRemovalConfirm() {
		this.ms.close('membership-removal-confirmation');
	}

	handleSoldOut(featureId) {
		const { items } = this.order;
		const orderItem = items.find(({ itemId }) => itemId === featureId);

		if (orderItem.itemType === 'featuredBook') {
			const feature = orderItem.item;

			const {
				ASIN,
				whereAvailable,
				description,
				_site,
				_siteAdvert,
				_book: {
					link,
					title,
					bookLength,
					nameOfSeries,
					bookNumber,
					numberOfBooksInSeries,
					threesomesOrRomanticScenes,
					endingOfBook,
					cheatingOrInfidelity,
					commonThemes,
				},
			} = feature;

			this.purchaseProcessService.setDetails({
				feature: {
					ASIN,
					whereAvailable,
				},
				book: {
					ASIN,
					amazonLink: `https://www.amazon.com/dp/${ASIN}`,
					whereAvailable,
					title,
					description,
					bookLength,
					nameOfSeries,
					bookNumber,
					numberOfBooksInSeries,
					threesomesOrRomanticScenes,
					endingOfBook,
					cheatingOrInfidelity,
					commonThemes,
					link,
				},
			}, 'soldout');

			let _link = `features/${_site.seoSlug}/${_siteAdvert.seoSlug}`;
			if (this.author && this.author.authorLink) {
				_link = `${AUTHOR_DASHBOARD}/${this.author.authorLink}/features/${_site.seoSlug}/${_siteAdvert.seoSlug}`;
			}
			this.os.setEditingItemId(feature._id);
			this.os.setCartNotVisible();
			this.router.navigate(['/']).then(() => this.router.navigate([_link], { queryParams: { feature: featureId } }).then());
		}

		if (orderItem.itemType === 'promoStack') {
			this.promoStackPurchaseService.editPromoStack(orderItem.item);
			const link = this.promoStackPurchaseService.getEditDateLink();
			const authorPath = this.author ? `${AUTHOR_DASHBOARD}/${this.author.authorLink}` : '';
			this.router.navigate([authorPath + link]);
		}
	}

	triggerEmailSchedule(orderItems) {
		const giveaways = orderItems.filter(({ item: { _siteAdvert } }) => _siteAdvert?.giveaway?.isGiveaway);

		if (giveaways.length) {
			giveaways.forEach(({ item: { _id, _siteAdvert } }) => {
				this.giveawayService.scheduleEmail(_id).subscribe();
			});
		}
	}

	/**
	 * The event parameter fired by the StripeForm.paymentSuccess event
	 * is a mess.
	 */
	_getAdditionalOrderDetails(event?: ProcessPaymentEvent): Partial<ICheckoutOrderData> {
		if (!event) {
			return {};
		}
		if (event.type === 'paypal') {
			const orderDetails: Partial<ICheckoutOrderData> = {
				payPalOrderID: event.orderID,
			};
			return orderDetails;
		}
		if (event.type === 'stripe') {
			const orderDetails: Partial<ICheckoutOrderData> = {
				paymentMethod: event.paymentMethod,
				// Is this even needed? Yes, yes it is.
				stripeCustomerId: this.author?.stripeCustomerId,
				savePaymentMethod: event.savePaymentMethod,
			};
			return orderDetails;
		}
		return {};
	}

	handleOutOfStockFeatures(invalidFeatureIds: string[], event?: ProcessPaymentEvent) {
		this.ls.postLog({
			type: event?.type || 'unknown-processor/free',
			message: 'Could not process purchase: Sold Out',
		});
		const invalidFeatures = this.order.items.filter(({ itemId }) => invalidFeatureIds.includes(itemId)) as IOrderFeatureItem[];
		const invalidFeatureMeta = {};
		invalidFeatures.forEach((feature) => {
			const { item: { _book, pubDate, _id } } = feature;
			invalidFeatureMeta[_id] = `${_book.title} by ${_book.author} on `
				+ `${moment(pubDate, 'YYYYMMDD').format('MMMM Do, YYYY')}.`;
		});
		this.paymentInProgress = false;
		this.soldOut = true;
		this.errorExtra = invalidFeatureMeta;

		this.errorOptions.title = 'Dang. That date is no longer available';
		this.errorOptions.text = [
			'The dates you selected for the following features are no longer available. Please select a new one.',
		];

		this.errorOptions.actions = [
			{
				type: 'solid',
				text: 'Pick Another Date',
				callback: () => {
					const invalidFeatureMetaIds = Object.keys(invalidFeatureMeta);

					this.ms.close('cart-error-modal');
					this.handleSoldOut(invalidFeatureMetaIds[0]);
				},
			},
		];

		this.ms.open('cart-error-modal');
	}

	handleAdvertsWithInvConflict(advertsWithInventoryConflict: string[], event?: ProcessPaymentEvent) {
		this.ls.postLog({
			type: event?.type || 'unknown-processor/free',
			message: 'Could not process purchase: Inventory conflict',
		});
		// There is an oversell conflict for a site advert
		const { text, title } = this.os.parseAdvertInventoryConflictError(advertsWithInventoryConflict, this.order);
		this.paymentInProgress = false;

		this.errorOptions.title = title;
		this.errorOptions.text = [text];
		this.ms.open('cart-error-modal');
	}

	handleInvalidPromoStacks(invalidPromoStackIds: TempInventoryResponse['invalidPromoStacks'], event?: ProcessPaymentEvent) {
		this.checkoutService.handleAdvertInventoryConflict(event, this.order, event?.type || 'unknown-processor/free');
		const invalidPromoStacks = this.order.items.filter((item): item is IOrderPromoStacksItem => {
			const { itemId } = item;
			return invalidPromoStackIds.some(({ orderPromoStackId }) => orderPromoStackId === itemId);
		});
		const errorExtra: { [k: string]: string } = invalidPromoStacks.reduce((acc, { itemId, item: { _promoStack, startDate } }) => {
			const dateLabel = _promoStack.releaseDuration > 1 ? 'starting' : '';

			acc[itemId] = `${_promoStack.name} ${dateLabel} on ${moment(startDate, 'YYYYMMDD').format('MMMM Do, YYYY')}`;
			return acc;
		}, {});

		this.paymentInProgress = false;
		this.soldOut = true;
		this.errorExtra = errorExtra;

		this.errorOptions.title = 'Dang. That date is no longer available';
		this.errorOptions.text = [
			'The promo stacks you selected are no longer available for the dates you selected. Please select a new date for your promo stack.',
		];

		this.errorOptions.actions = [
			{
				type: 'solid',
				text: 'Pick Another Date',
				callback: () => {
					const invalidFeatureMetaIds = Object.keys(errorExtra);

					this.ms.close('cart-error-modal');
					this.handleSoldOut(invalidFeatureMetaIds[0]);
				},
			},
		];
	}

	/**
	 * Runs some validators before processing payment and displays an error modal
	 * if any validator fails.
	 *
	 * Not the most elegant way to do it but it works.
	 */
	runValidators(): boolean {
		const hasCartSubscription = this.order.items.some((item) => item.itemType === 'subscription');
		const thirtyDayError = this.os.checkOrderForThirtyDayViolation(this.order);
		if (hasCartSubscription && !this.author) {
			this.errorOptions.text = ['You must create an account or login to purchase a subscription.'];
			this.ms.open('cart-error-modal');
			return false;
		}
		if (thirtyDayError && thirtyDayError.errorText) {
			this.errorOptions.text = [thirtyDayError.errorText];
			this.ms.open('cart-error-modal');
			return false;
		}
		return true;
	}

	async handlePaymentCancelled() {
		this.paymentInProgress = false;
	}

	async handleProcessPayment(event?: ProcessPaymentEvent) {
		if (!this.runValidators()) {
			return;
		}
		const orderDetails: ICheckoutOrderData = {
			type: event?.type,
			orderId: this.order._id,
			url: window.location.href,
			/**
			 * No idea what this is or why it's only sent by free and stripe
			 * handlers but not paypal.
			 * I'll always send it just in case.
			 */
			messageId: this.localStorageService.getData('messageId'),
			...this._getAdditionalOrderDetails(event),
		};
		this.paymentInProgress = true;

		this.os.checkOrderSiteInventory(this.order._id).subscribe({
			next: (response) => {
				/**
				 * Failed to check inventory
				 */
				if (!response.success) {
					this.paymentInProgress = false;
					const errorMsg = response.message || 'Something went wrong.';
					this.errorOptions.text = [errorMsg];
					this.ms.open('cart-error-modal');
					return;
				}
				if (response.invalidFeatures?.length) {
					this.handleOutOfStockFeatures(response.invalidFeatures, event);
					return;
				}
				if (response.advertsWithInventoryConflict?.length) {
					this.handleAdvertsWithInvConflict(response.advertsWithInventoryConflict, event);
					return;
				}
				if (response.invalidPromoStacks?.length) {
					this.handleInvalidPromoStacks(response.invalidPromoStacks, event);
				}
				this.os.processPayment(orderDetails).subscribe({
					next: (paymentResponse) => {
						this.handlePaymentResponse(paymentResponse, event);
					},
					error: (_) => {
						this.paymentInProgress = false;
					},
				});
			},
			error: (_) => {

			},
		});
	}

	async handlePaymentResponse(response: ProcessPaymentAPIResponse, event?: ProcessPaymentEvent) {
		if (!response.success && event?.type === 'stripe' && response.requires_action) {
			await this.handleRequiredStripeAction(response, event);
			return;
		}
		if (!response.success && event?.type === 'paypal' && response.requires_action) {
			/**
			 * I'm only doing this because the paypal buttons component is a child
			 * of this one and I don't have direct access to the sdk to restart the proces
			 * like stripe provides.
			 */
			event.actions.restart();
			return;
		}
		if (!response.success) {
			this.handlePaymentError(response, event);
			return;
		}
		this.handlePaymentSuccess(response, event);
	}

	handlePaymentError(paymentResponse: ProcessPaymentAPIResponse, event?: ProcessPaymentEvent) {
		this.paymentInProgress = false;
		if (event?.type === 'stripe') {
			const errorMessage = paymentResponse.error?.raw?.message;
			this.errorOptions.text = [errorMessage || paymentResponse.message || 'Something went wrong'];
		}
		if (event?.type === 'paypal') {
			const errorMessage = paymentResponse.error?.message;
			this.errorOptions.text = [errorMessage || paymentResponse.message || 'Something went wrong'];
			this.ls.postLog({
				type: event?.type || 'unknown-processor/free',
				message: `Failed to post transaction to PayPal for feature: ${this.order._id}.`,
			});
		}
		this.ms.open('cart-error-modal');
	}

	handlePaymentSuccess(_: ProcessPaymentAPIResponse, event?: ProcessPaymentEvent) {
		/**
		 * I have absolutely no idea why this is only triggered for stripe payments
		 * in the legacy methods.
		 */
		if (event?.type === 'stripe') {
			this.triggerEmailSchedule(this.order.items);
		}
		if (event?.type === 'stripe' && event.savePaymentMethod) {
			/**
			 * Prevent saving when emulating an user in prod.
			 */
			if (this.us.isEmulated) {
				return;
			}
			this.savedPaymentMethodsService.create({
				_user: this.author._id,
				paymentMethodId: event.paymentMethod,
			}).subscribe();
		}
		this.ls.postLog({
			type: event?.type || 'unknown-processor/free',
			message: `Transaction completed for feature: ${this.order._id}`,
		});
		this.triggerPurchaseAnalytics(event?.type ?? 'coupon');
		let paymentSource;
		if (event) {
			paymentSource = event.type === 'stripe' ? 'Stripe' : 'PayPal';
		} else {
			paymentSource = 'Coupon';
		}
		this.checkoutService.handleSuccess(this.order, paymentSource, this.orderTotalAmount);
		// Update the promo code for the Discount Meter.
		this.promoCodeService.triggerUpdatePromoCode();
		this.router.navigate(['success'], { relativeTo: this.route }).then();
		this.os.clearStoreOrder();
		this.paymentInProgress = false;
	}

	/**
	 * Some payment methods require further user action like 3d secure before
	 * confirming the payment, this method triggers those actions and retries payment processing.
	 */
	async handleRequiredStripeAction(response: ProcessPaymentAPIResponse, event?: ProcessPaymentEvent) {
		if (event?.type === 'stripe') {
			const stripe = await this.stripeService.getInstance();
			const { client_secret: clientSecret } = response.token.stripeToken;
			const { error, paymentIntent } = await stripe.handleCardAction(clientSecret);
			if (error) {
				this.errorOptions.text = [error.message];
				this.ms.open('cart-error-modal');
				this.paymentInProgress = false;
				return;
			}
			const orderDetails: ICheckoutOrderData = {
				type: event?.type,
				orderId: this.order._id,
				url: window.location.href,
				messageId: this.localStorageService.getData('messageId'),
				/**
				 * This time we send the payment intent we had created
				 * instead of the payment method and customer to create a new one.
				 */
				paymentIntent: paymentIntent.id,
			};
			this.os.processPayment(orderDetails).subscribe({
				next: (paymentResponse) => {
					/**
					 * This can recursively call handleRequiredAction if
					 * further actions are required.
					 */
					this.handlePaymentResponse(paymentResponse, event);
				},
				error: (_) => {
					this.paymentInProgress = false;
				},
			});
		}
	}

	startOver() {
		this.purchaseProcessService.clearDetails();
		this.router.navigate([FEATURES_PATH]).then();
	}

	/**
	 * This function seems to be using a legacy version of analytics, we should
	 * consider migrating to the new methods.
	 */
	triggerPurchaseAnalytics(paymentProcessor?: 'stripe' | 'paypal' | 'coupon') {
		const checkoutStep = 3;
		// ecommerce object for GTM
		const promoCode = [];
		const cartItems = this.order.items.map(({ item, itemType }) => {
			if (itemType !== 'featuredBook' && itemType !== 'promoStack') {
				return null;
			}
			let { paymentAmount } = item;
			if (item._promoCode && typeof item._promoCode === 'object') {
				promoCode.push(`${item._promoCode.code}`);
				if (item._promoCode.discountType === PROMO_TYPE_PERCENT) {
					const promoAmount = Math.round(((paymentAmount * item._promoCode.value) / 100) * 100) / 100;
					paymentAmount -= promoAmount;
				} else if (item._promoCode.discountType === PROMO_TYPE_AMOUNT) {
					const promoAmount = item._promoCode.value;
					paymentAmount -= promoAmount;
				}
			}

			if (('_siteAdvert' in item) && item._siteAdvert && item._siteAdvert._site) {
				return {
					id: item._siteAdvert.seoSlug,
					name: `${item._site.name} ${item._siteAdvert.name} Feature`,
					category: `${item._site.name}/${item._siteAdvert.name}`,
					price: paymentAmount,
					quantity: 1,
					dimension1: item._book.author,
					dimension2: this.author ? this.author._id : '',
				};
			}

			return null;
		}).filter((item) => !!item);

		if (!cartItems.length) {
			return;
		}

		const ecommerce = {
			currencyCode: 'USD',
			purchase: {
				actionField: {
					id: this.order._id,
					coupon: promoCode.length ? `${promoCode.join(',')}` : null,
					revenue: this.order.totalAmount,
				},
				products: cartItems,
			},
		};
		this.as.trackCheckoutOption(checkoutStep, paymentProcessor);
		this.as.trackGTMEvent('eec.purchase', { ecommerce });
	}

	_qualifyForMembershipUpsell(shouldDisplayUpsell) {
		if (!shouldDisplayUpsell) {
			return false;
		}

		// If the user is not a member, and the cart contains a subscription
		// and the cart does not contain a membership upsell
		if (this.author && this.author?.memberRole?.roleName !== 'Free') {
			return false;
		}

		const existingSubscriptionInCart = this.os.getSubscriptionFromOrder();

		if (existingSubscriptionInCart) {
			return false;
		}

		if (!this.author && !this.checkoutSignupLogin) {
			return false;
		}

		return true;
	}
}
